<template>
  <transition name="lh-fade">
    <div class="lh-overlay" v-if="visible" role="dialog" aria-modal="true" @click.self="close">
      <div class="lh-box" ref="box" @click.stop>
        <button class="lh-close" @click="close" aria-label="Close">
          <i class="fa-light fa-times"></i>
        </button>

        <div class="announcement-icon">
          <i class="fa-sharp fa-light fa-megaphone"></i>
        </div>
        <div class="lh-loading" v-if="loading">
          Loading...
        </div>

        <div class="lh-header" v-else-if="post">
          <h2 class="lh-title" v-html="post.title.rendered"></h2>
          <hr>
          <div class="lh-meta">
            <span>{{ formattedDate }}</span>
            <span v-if="post.author_name"> | By {{ post.author_name }}</span>
          </div>
        </div>

        <div class="lh-body" v-if="post" v-html="processedContent"></div>

        <div class="lh-empty" v-else-if="!loading">
          Post not found.
        </div>
      </div>
    </div>
  </transition>
</template>

<script>
import { safeFetch } from '@/utils/fetch';

const POST_TYPE_ENDPOINTS = {
  post: '/wp-json/wp/v2/posts/',
  announcement: '/wp-json/wp/v2/announcement/',
  event: '/wp-json/wp/v2/events/',
  goal: '/wp-json/wp/v2/goal/',
  process: '/wp-json/wp/v2/process/',
  training: '/wp-json/wp/v2/training/',
};

export default {
  name: 'Lightbox',
  props: {
    visible: { type: Boolean, default: false },
    slug: { type: String, default: null },
    type: { type: String, default: 'post' }
  },
  data() {
    return {
      post: null,
      loading: false,
      postType: this.type || 'post'
    };
  },
  watch: {
    visible(val) {
      if (val) this.open();
      else this.cleanup();
    },
    slug() {
      if (this.visible) this.fetchPost();
    },
    type(val) {
      this.postType = val || 'post';
    }
  },
  computed: {
    formattedDate() {
      if (!this.post || !this.post.date) return '';
      const date = new Date(this.post.date);
      return date.toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' });
    },
    processedContent() {
      if (!this.post || !this.post.content || !this.post.content.rendered) return '';
      const html = this.post.content.rendered;
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      const legends = doc.querySelectorAll('.gform_wrapper legend');
      legends.forEach(legend => {
        const label = document.createElement('label');
        if (legend.id) label.id = legend.id;
        if (legend.className) label.className = legend.className;
        label.innerHTML = legend.innerHTML;
        legend.parentNode.replaceChild(label, legend);
      });
      return doc.body.innerHTML;
    }
  },
  mounted() {
    if (this.visible) this.open();
  },
  beforeUnmount() {
    this.cleanup();
  },
  methods: {
    open() {
      this.postType = this.type || 'post';
      this.addKeyListener();
      this.fetchPost();
      document.body.classList.add('lh-no-scroll');
    },
    close() {
      this.$emit('update:visible', false);
      this.$emit('close');
      this.post = null;
      this.loading = false;
      this.cleanup();
    },
    cleanup() {
      this.removeKeyListener();
      document.body.classList.remove('lh-no-scroll');
    },
    onKeydown(e) {
      if (e.key === 'Escape' && this.visible) this.close();
    },
    addKeyListener() {
      window.addEventListener('keydown', this.onKeydown);
    },
    removeKeyListener() {
      window.removeEventListener('keydown', this.onKeydown);
    },
    async fetchPost() {
      if (!this.slug) {
        this.post = null;
        return;
      }
      this.loading = true;
      this.post = null;
      const endpoint = `${POST_TYPE_ENDPOINTS[this.postType] || POST_TYPE_ENDPOINTS.post}`;
      try {
        const token = localStorage.getItem('authToken');
        const res = await safeFetch(`${endpoint}?slug=${encodeURIComponent(this.slug)}&_embed`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (!res.ok) throw new Error('Not found');
        const posts = await res.json();
        const post = posts && posts[0] ? posts[0] : null;
        if (post && post._embedded && post._embedded.author && post._embedded.author[0]) {
          post.author_name = post._embedded.author[0].name;
        }
        this.post = post;
      } catch (err) {
        this.post = null;
      } finally {
        this.loading = false;
      }
    }
  }
};
</script>

<style scoped>
.lh-overlay {
  position: fixed;
  inset: 0;
  background: rgba(12, 18, 24, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1050;
  padding: 1.25rem;
}
.lh-box {
  width: 580px;
  max-width: 100%;
  max-height: 90vh;
  min-height: 300px;
  overflow: auto;
  background: #fff;
  border-radius: 38px;
  position: relative;
  padding: 1.25rem 1.5rem;
  box-shadow: 0 12px 30px rgba(0,0,0,0.35);
}
.lh-close {
  position: absolute;
  top: 20px;
  right: 20px;
  border: none;
  background-color: #E1DED8;
  border-radius: 50%;
  font-size: 1.05rem;
  color: #666;
  cursor: pointer;
  padding: 3px 6px 1px 6px;
}
.lh-title {
  margin: 0 0 0.5rem;
  font-size: 20px;
}
.lh-meta {
  color: #777;
  font-size: 0.9rem;
  margin-bottom: 1rem;
}
.lh-body {
  text-align: left;
  color: #5F5E5B;
  font-size: 18px;
}
.lh-empty, .lh-header {
  text-align: left;
  color: #666;
  padding: 1rem 0 0 0;
}
.lh-loading {
  text-align: center;
  color: #666;
  padding: 1rem 0;
}

/* Prevent background scroll while open */
.lh-no-scroll {
  overflow: hidden;
}
hr {
  border: none;
  height: 2px; 
  background-color: #94918BA6;
}

/* simple fade */
.lh-fade-enter-active, .lh-fade-leave-active { transition: opacity 180ms ease; }
.lh-fade-enter, .lh-fade-leave-to { opacity: 0; }

.announcement-icon {
  width: 80px;
  height: 80px;
  min-width: 80px;
  min-height: 80px;
  border-radius: 50%;
  background: #025780;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 0.75rem;
  font-size: 2.5rem;
  color: #fff;
  box-sizing: border-box;
}
</style>